import axios from "axios";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Input, Form } from "antd";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Swal from "sweetalert2";

interface IProps {
  isRegister: boolean;
  setIsRegister: () => void;
  setIsShowLogin: () => void;
}

const schema = yup.object().shape({
  username: yup.string().required("Tên tài khoản bắt buộc"),
  managedBy: yup.string().required("Đại lý bắt buộc"),
  phone_number: yup
    .string()
    .matches(/^\d{10}$/, "Số điện thoại phải có 10 chữ số")
    .required("Số điện thoại bắt buộc"),
  password: yup.string().min(6, "Mật khẩu ít nhất 6 ký tự").required(),
});

const Register: React.FC<IProps> = ({
  isRegister,
  setIsRegister,
  setIsShowLogin,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleRegister = async (data: any) => {
      try {
        await axios
          .post(`${process.env.REACT_APP_URL_API}/auth/register`, {
            username: data.username,
            managedByUsername: data.managedBy,
            phone: data.phone_number,
            password: String(data.password),
          })
          .then((data) => {
            if (data.status === 201) {
              Swal.fire({
                icon: "success",
                title: "Đăng ký thành công",
                text: "Vui lòng đợi ít phút. ",
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  htmlContainer: "custom-text",
                },
              });
              setIsRegister();
              setIsShowLogin();
              reset();
            }
          })
          .catch((err) => {
            if (
              err.response.data.message[0] === "managedBy must be a mongodb id"
            ) {
              Swal.fire({
                icon: "error",
                title: "Lỗi đăng ký",
                text: "Thông tin đại lý chưa chính xác!",
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  htmlContainer: "custom-text",
                },
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Lỗi đăng ký",
                text: `${err.response.data.message}`,
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  htmlContainer: "custom-text",
                },
              });
            }
          });
      } catch (error) {
        return error;
      }
  };

  return (
    <div
      className="register-overlay"
      style={{
        position: "fixed",
        inset: 0,
        display: isRegister ? "flex" : "none",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.85)",
      }}
    >
      <div
        className="register-modal-box"
        style={{
          position: "relative",
          width: 487,
          maxWidth: "95vw",
          boxSizing: "border-box",
          background: "#430000E5",
          border: "2px solid #dc2626",
          borderRadius: 12,
          padding: "24px 28px",
          boxShadow:
            "0 0 30px rgba(220, 38, 38, 0.25), inset 0 0 60px rgba(0,0,0,0.2)",
          fontFamily: "'Courier New', monospace",
        }}
      >
        <button
          type="button"
          onClick={setIsRegister}
          aria-label="Đóng đăng ký"
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            color: "#ef4444",
            fontSize: 22,
            cursor: "pointer",
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          ×
        </button>

        <h2
          style={{
            textAlign: "center",
            color: "#fff",
            fontSize: 22,
            fontWeight: "bold",
            marginBottom: 20,
            textShadow: "0 0 12px rgba(255,255,255,0.4)",
            letterSpacing: "2px",
          }}
        >
          ĐĂNG KÝ
        </h2>

        <Form
          id="regisform"
          onFinish={handleSubmit(handleRegister)}
          style={{ textAlign: "center" }}
        >
          {/* Tài khoản */}
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <label
              style={{
                display: "block",
                color: "rgba(255,255,255,0.9)",
                fontSize: 13,
                marginBottom: 6,
                fontWeight: "bold",
              }}
            >
              TÀI KHOẢN
            </label>
            <Form.Item
              validateStatus={errors.username ? "error" : ""}
              help={errors.username?.message}
              style={{ marginBottom: 0 }}
            >
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    placeholder="Nhập tên đăng nhập"
                    maxLength={16}
                    autoComplete="username"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      background: "#420101",
                      border: "1px solid #dc2626",
                      borderRadius: 8,
                      color: "#fff",
                      fontSize: 14,
                      fontFamily: "'Courier New', monospace",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                )}
              />
            </Form.Item>
          </div>

          {/* Số điện thoại */}
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <label
              style={{
                display: "block",
                color: "rgba(255,255,255,0.9)",
                fontSize: 13,
                marginBottom: 6,
                fontWeight: "bold",
              }}
            >
              SỐ ĐIỆN THOẠI
            </label>
            <Form.Item
              validateStatus={errors.phone_number ? "error" : ""}
              help={errors.phone_number?.message}
              style={{ marginBottom: 0 }}
            >
              <Controller
                name="phone_number"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    placeholder="Nhập số điện thoại"
                    maxLength={10}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      background: "#420101",
                      border: "1px solid #dc2626",
                      borderRadius: 8,
                      color: "#fff",
                      fontSize: 14,
                      fontFamily: "'Courier New', monospace",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                )}
              />
            </Form.Item>
          </div>

          {/* Mật khẩu */}
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <label
              style={{
                display: "block",
                color: "rgba(255,255,255,0.9)",
                fontSize: 13,
                marginBottom: 6,
                fontWeight: "bold",
              }}
            >
              MẬT KHẨU
            </label>
            <Form.Item
              validateStatus={errors.password ? "error" : ""}
              help={errors.password?.message}
              style={{ marginBottom: 0 }}
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <div style={{ position: "relative" }}>
                    <input
                      {...field}
                      type={showPassword ? "text" : "password"}
                      placeholder="Nhập mật khẩu"
                      maxLength={16}
                      autoComplete="new-password"
                      style={{
                        width: "100%",
                        padding: "10px 36px 10px 12px",
                        background: "#420101",
                        border: "1px solid #dc2626",
                        borderRadius: 8,
                        color: "#fff",
                        fontSize: 14,
                        fontFamily: "'Courier New', monospace",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 4,
                        color: "rgba(255,255,255,0.7)",
                      }}
                      aria-label={
                        showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {showPassword ? (
                          <>
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </>
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                )}
              />
            </Form.Item>
          </div>

          {/* Mã giới thiệu */}
          <div style={{ marginBottom: 18, textAlign: "left" }}>
            <label
              style={{
                display: "block",
                color: "rgba(255,255,255,0.9)",
                fontSize: 13,
                marginBottom: 6,
                fontWeight: "bold",
              }}
            >
              MÃ GIỚI THIỆU
            </label>
            <Form.Item
              validateStatus={errors.managedBy ? "error" : ""}
              help={errors.managedBy?.message}
              style={{ marginBottom: 0 }}
            >
              <Controller
                name="managedBy"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    placeholder="Nhập mã giới thiệu"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      background: "#420101",
                      border: "1px solid #dc2626",
                      borderRadius: 8,
                      color: "#fff",
                      fontSize: 14,
                      fontFamily: "'Courier New', monospace",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                )}
              />
            </Form.Item>
          </div>

          {/* Nút Đăng ký */}
          <div style={{ marginTop: 12 }}>
            <button
              type="submit"
              style={{
                width: "100%",
                padding: "14px",
                background: "linear-gradient(90deg, #dc2626, #ea580c)",
                border: "none",
                borderRadius: 8,
                color: "#fff",
                fontSize: 15,
                fontWeight: "bold",
                fontFamily: "'Courier New', monospace",
                cursor: "pointer",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: "0 2px 8px rgba(220,38,38,0.4)",
              }}
            >
              [ ĐĂNG KÝ ]
            </button>
          </div>
        </Form>

        {/* Đã có tài khoản? Đăng nhập ngay */}
        <div
          style={{
            marginTop: 20,
            paddingTop: 12,
            borderTop: "1px solid rgba(248, 113, 113, 0.4)",
            textAlign: "center",
          }}
        >
          <p
            style={{
              color: "rgba(255,255,255,0.9)",
              fontSize: 13,
              marginBottom: 6,
            }}
          >
            ĐÃ CÓ TÀI KHOẢN?
          </p>
          <span
            role="button"
            tabIndex={0}
            onClick={() => {
              setIsRegister();
              setIsShowLogin();
            }}
            onKeyDown={(e) => e.key === "Enter" && (setIsRegister(), setIsShowLogin())}
            style={{
              color: "#facc15",
              fontSize: 14,
              cursor: "pointer",
              fontWeight: "bold",
              textDecoration: "underline",
            }}
          >
            &gt;&gt;ĐĂNG NHẬP NGAY&lt;&lt;
          </span>
        </div>
      </div>
    </div>
  );
};

export default Register;
